import { useEffect, useRef, useState } from "react";
import { MessageAttachment } from "../../features/messages/models/MessageAttachment";
import { MessagesChannel } from "../../features/messages/models/MessagesChannel";
import { useOpenAttachment } from "./useOpenAttachment";

interface InlineImageProps {
  attachment: MessageAttachment;
  channel: MessagesChannel;
}

/**
 * A photo in a hairline frame, at most 320 px wide; its short-lived URL is fetched when the image
 * is shown (the message list never carries one). A tap opens it full size in a new tab.
 */
const InlineImage = ({ attachment, channel }: InlineImageProps) => {
  const { fetchUrl, inNewTab } = useOpenAttachment(channel);
  const [src, setSrc] = useState<string | null>(null);
  // The URL is fetched once per attachment, not on every render that recreates the fetcher.
  const fetchOnce = useRef(fetchUrl);
  const ratio = attachment.width && attachment.height ? `${attachment.width} / ${attachment.height}` : undefined;

  useEffect(() => {
    let live = true;
    void fetchOnce.current(attachment.id).then((url) => live && setSrc(url));
    return () => {
      live = false;
    };
  }, [attachment.id]);

  return (
    <button
      type="button"
      className="block w-full max-w-[320px] overflow-hidden rounded-lg border border-grey-90 bg-grey-95 dark:border-primary-20 dark:bg-primary-20"
      style={{ aspectRatio: ratio }}
      onClick={() => void inNewTab(attachment.id)}
      aria-label={attachment.fileName}
      data-testid="message-image"
    >
      {src && <img src={src} alt={attachment.fileName} className="block h-full w-full object-cover" />}
    </button>
  );
};

export default InlineImage;
